import { safePattern, type Field } from "./schema";
export const presetWritable = (field: Field) =>
  field.editable &&
  !field.system &&
  !field.calculation &&
  field.source.kind !== "lookup";
export function presetTarget(fields: readonly Field[], key: string): Field {
  const matches = fields.filter(
    (field) => field.id === key || field.key === key,
  );
  if (matches.length !== 1)
    throw new Error(
      matches.length
        ? `Ambiguous preset target: ${key}`
        : `Unknown preset target: ${key}`,
    );
  return matches[0];
}
/** Each assigned field depends on its selector, including part-specific records. */
export function presetDependencies(
  fields: readonly Field[],
): Map<string, string[]> {
  const result = new Map<string, string[]>();
  for (const selector of fields) {
    if (
      new Set(selector.presets.map((record) => record.id)).size !==
      selector.presets.length
    )
      throw new Error(`Duplicate preset options for ${selector.label}`);
    if (selector.type !== "preset") continue;
    for (const record of selector.presets) {
      if (record.match) {
        if (!fields.some((f) => f.id === record.match!.field))
          throw new Error("Unknown preset match variable");
        if (record.match.field === selector.id)
          throw new Error("Preset cannot match its own selector");
        if (record.match.op === "matches") safePattern(record.match.value);
        const refs = result.get(selector.id) || [];
        if (!refs.includes(record.match.field)) refs.push(record.match.field);
        result.set(selector.id, refs);
      }
      for (const key of Object.keys(record.values)) {
        const target = presetTarget(fields, key);
        if (target.id === selector.id)
          throw new Error("Preset cannot assign its own selector");
        if (!presetWritable(target)) continue;
        const dependencies = result.get(target.id) || [];
        if (!dependencies.includes(selector.id)) dependencies.push(selector.id);
        result.set(target.id, dependencies);
      }
    }
  }
  return result;
}
